import type Widget from "@/model/widget";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import WidgetCard from "./widget-card";
import { CircleArrowRight, LoaderCircle, ReceiptText } from "lucide-react";
import {
  useCareHistoryClaimsQuery,
  useCareHistoryConfigQuery,
  useEOBQuery,
} from "@/hooks/use-care-history";
import { formatDateToLabel } from "@/lib/date-utils";
import {
  CARE_HISTORY_PERIOD_FLAG,
  CLAIM_TYPES,
  CLAIMS_STATUS,
  EOB_VERBIAGE,
  GROUP_ID,
  MIME_TYPE,
  QueryKeys,
  RootWidgetTypes,
} from "@/constants";
import useUserInfo from "@/hooks/use-user-info";
import {
  formatToCurrency,
  openFileFromBase64,
  openPendingWindow,
} from "@/lib/utils";
import { queryClient } from "@/hooks/use-query-client";
import { Button } from "@/components/ui/button";
import { NoDataAvailable } from "@/pages/components/empty";
import ScreenRouteNames from "@/constants/screen-route-names";
import { ErrorNotificationType } from "../error-notification";
import { useNotificationError } from "@/context/error-provider";
import type { RootState } from "@/store/store";
import { useSelector } from "react-redux";
import { useFeatureName } from "@/hooks/use-pageTitle";
import {
  capitalizeInitialLetter,
  lowerCaseComparison,
} from "@/lib/string-util";
import IDENTIFIERS from "@/constants/test_id";
import type { LogContext } from "@/services/logger/logger-types";
import {
  CustomLogTitle,
  LogCategories,
  LogEventTypeEnum,
  LogSubCategories,
  LogTags,
} from "@/services/logger/logger-types";
import { useActionLogger } from "@/services/logger/logger-hooks";

type RecentVisitWidgetProps = {
  className?: string;
  widget: Widget;
};

type selectedEOBState = {
  selectedRevClaimId: string;
  selectedMemberId: string;
};

const RecentVisitWidget: React.FunctionComponent<RecentVisitWidgetProps> = ({
  widget,
}) => {
  const navigate = useNavigate();
  const userInfo = useUserInfo();
  const { logAction } = useActionLogger();
  const { addError } = useNotificationError();
  const { activeCoverage } = useSelector(
    (state: RootState) => state?.planCoverageInfo,
  );
  const [selectedEOB, setSelectedEOB] = useState<selectedEOBState>({
    selectedRevClaimId: "",
    selectedMemberId: "",
  });

  const pendingWindowRef = useRef<Window | null>(null);
  const claimType = lowerCaseComparison(
    widget?.rootWidgetId,
    RootWidgetTypes.RxClaims,
  )
    ? CLAIM_TYPES?.PHARMACY.VALUE
    : CLAIM_TYPES.MEDICAL.VALUE;
  const { data: config } = useCareHistoryConfigQuery();
  const claimsFromDate = config?.periodStartDate;
  const claimsToDate = config?.periodEndDate;

  const {
    isLoading: isClaimsLoading,
    data: claimsData,
    isFetching: isClaimsFetching,
  } = useCareHistoryClaimsQuery(
    claimsFromDate!,
    claimsToDate!,
    CARE_HISTORY_PERIOD_FLAG.MONTHLY,
    activeCoverage,
  );
  const { isFetching: isEOBLoading, refetch } = useEOBQuery(
    selectedEOB?.selectedRevClaimId,
    selectedEOB?.selectedMemberId,
    activeCoverage?.claimCoverageCode,
    activeCoverage?.planId,
  );

  useEffect(() => {
    if (
      selectedEOB?.selectedMemberId?.length > 0 &&
      selectedEOB?.selectedRevClaimId?.length > 0
    ) {
      fetchEOB({
        claimNumber: selectedEOB?.selectedRevClaimId,
        memberId: selectedEOB?.selectedMemberId,
        claimCoverageCode: activeCoverage?.claimCoverageCode,
        planId: activeCoverage?.planId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEOB]);

  const fetchEOB = async (logMetaData: Record<string, unknown>) => {
    let cachedEOB = queryClient.getQueryData<string>([
      QueryKeys.EOB,
      selectedEOB?.selectedRevClaimId,
    ]);
    const pendingWindow = pendingWindowRef.current;
    pendingWindowRef.current = null;
    if (!cachedEOB) {
      const { data: eobData } = await refetch();
      cachedEOB = eobData;
      setSelectedEOB({ selectedRevClaimId: "", selectedMemberId: "" });
      await openEOB(eobData!, logMetaData, pendingWindow);
    } else {
      setSelectedEOB({ selectedRevClaimId: "", selectedMemberId: "" });
      await openEOB(cachedEOB, logMetaData, pendingWindow);
    }
  };

  const openEOB = async (
    eob: string,
    logMetaData: Record<string, unknown>,
    pendingWindow: Window | null,
  ) => {
    const logOptions: LogContext = {
      tag: LogTags.MODULE_ACCESS,
      category: LogCategories.RECENT_MEDICAL_WIDGET,
      subCategory: LogSubCategories.NONE,
      type: LogEventTypeEnum.PAGE_VIEW,
    };
    if (eob?.length > 10000) {
      logAction(
        capitalizeInitialLetter(CustomLogTitle.EOB_CARD),
        logMetaData,
        logOptions,
      );
      const isOpened = await openFileFromBase64(
        eob,
        MIME_TYPE.PDF,
        pendingWindow,
      );
      if (!isOpened) {
        pendingWindow?.close();
        addError({
          message: EOB_VERBIAGE.EOB_PENDING,
          type: ErrorNotificationType.ERROR,
        });
      }
    } else {
      pendingWindow?.close();
      addError({
        message: EOB_VERBIAGE.EOB_PENDING,
        type: ErrorNotificationType.ERROR,
      });
    }
  };

  const handleViewEOB = (revClaimId: string, memberId: string) => {
    const cachedEOB = queryClient.getQueryData<string>([
      QueryKeys.EOB,
      revClaimId,
    ]);

    pendingWindowRef.current =
      !cachedEOB || cachedEOB.length > 10000 ? openPendingWindow() : null;
    setSelectedEOB({
      selectedRevClaimId: revClaimId,
      selectedMemberId: memberId,
    });
  };

  const renderRecentVisit = () => {
    const recentVisit = claimsData
      ?.flatMap((obj) => obj?.historyList)
      .filter((claim) => lowerCaseComparison(claim.claimType, claimType))
      .slice(0, 2);
    return recentVisit && recentVisit.length > 0
      ? recentVisit?.map((recentVisit, i) => {
          return (
            <div
              key={`recent-visit-widget-item-${i}`}
              className="w-full space-y-1 px-2 py-1"
            >
              <div className="flex items-start justify-between py-2">
                <div className="flex-1">
                  <p className="mb-1 text-sm text-gray-500">
                    {formatDateToLabel(recentVisit?.recentSvcDate)}
                  </p>
                  <p className="mb-1 text-sm text-gray-500">
                    {lowerCaseComparison(
                      recentVisit?.claimType,
                      CLAIM_TYPES.PHARMACY.VALUE,
                    )
                      ? recentVisit?.drugName
                      : recentVisit?.pointOfService}
                  </p>
                  <p className="mb-1 text-medium font-semibold text-gray-900">
                    {recentVisit?.prvFullName}
                  </p>
                  <p className="text-sm text-gray-500">
                    {lowerCaseComparison(userInfo?.groupId, GROUP_ID.SOMEHA)
                      ? "Not Available"
                      : recentVisit?.prvAddress1}
                  </p>
                  {recentVisit?.showEob ? (
                    <Button
                      onClick={() =>
                        handleViewEOB(
                          recentVisit?.revClaimId,
                          recentVisit?.memberId,
                        )
                      }
                      variant="secondary"
                      className="rounded-6 mt-2 border-0 bg-primary px-6 py-2 text-sm font-normal text-white hover:bg-primary-selected"
                      data-testid={
                        IDENTIFIERS.CARE_AND_CLAIMS_HISTORY.VIEW_EOB_BUTTON
                      }
                      logCategory={LogCategories.RECENT_MEDICAL_WIDGET}
                      logMetadata={{
                        claimNumber: recentVisit?.revClaimId,
                        memberId: recentVisit?.memberId,
                        claimCoverageCode: activeCoverage?.claimCoverageCode,
                        planId: activeCoverage?.planId,
                      }}
                    >
                      {isEOBLoading &&
                      lowerCaseComparison(
                        selectedEOB.selectedRevClaimId,
                        recentVisit?.revClaimId,
                      ) ? (
                        <LoaderCircle className="text-white-500 size-6 animate-spin" />
                      ) : (
                        "View EOB"
                      )}
                    </Button>
                  ) : null}
                </div>
                <div className="text-right">
                  <span className="font-semibold text-gray-900">
                    {lowerCaseComparison(
                      recentVisit?.totalOopPaid,
                      CLAIMS_STATUS.PENDING,
                    )
                      ? recentVisit?.totalOopPaid
                      : formatToCurrency(parseFloat(recentVisit?.totalOopPaid))}
                  </span>
                </div>
              </div>
            </div>
          );
        })
      : undefined;
  };

  const renderSkeletonLoader = () => {
    return (
      <div className="w-full animate-pulse space-y-4 p-4">
        <div className="flex items-start justify-between border-b border-gray-100 py-4">
          <div className="flex-1 space-y-2">
            <div className="h-4 w-24 rounded bg-gray-200" />
            <div className="h-4 w-20 rounded bg-gray-200" />
            <div className="h-4 w-32 rounded bg-gray-200" />
            <div className="h-4 w-40 rounded bg-gray-200" />
          </div>
          <div className="text-right">
            <div className="h-6 w-16 rounded bg-gray-200" />
          </div>
        </div>
      </div>
    );
  };

  const renderWidget = () => {
    if (isClaimsFetching || isClaimsLoading) {
      return renderSkeletonLoader();
    } else {
      return (
        renderRecentVisit() ?? (
          <NoDataAvailable
            message="There is currently no claims data to display."
            icon={ReceiptText}
          />
        )
      );
    }
  };

  const viewFullCareHistory = () => {
    const targetPath = `/${ScreenRouteNames.CARE_HISTORY.path}`;
    navigate(targetPath, {
      replace: false,
    });
  };

  const footerContent = (
    <Button
      variant="link"
      className="w-full hover:underline"
      onClick={viewFullCareHistory}
    >
      View Full {useFeatureName(ScreenRouteNames.CARE_HISTORY.path)}{" "}
      <CircleArrowRight className="ml-1" />
    </Button>
  );

  return (
    <WidgetCard title={widget?.widgetDisplayName} footerContent={footerContent}>
      {renderWidget()}
    </WidgetCard>
  );
};

export default RecentVisitWidget;
